import type { ReviewWithReplyDto } from '@up2stay/up2promo-api-client';
import { Badge, Button, Card, cn } from '@up2stay/up2promo-ui';
import { Loader2, Pencil, Reply, Sparkles, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { formatRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import { StarRating } from '@/core/components/reputation/reputation-bits';
import { ReviewAuthorAvatar } from '@/core/components/reputation/reviews/review-list-item';
import { ReviewReplyComposer } from '@/core/components/reputation/reviews/review-reply-composer';
import {
  isUrgentReview,
  type ReviewsInbox,
} from '@/core/components/reputation/reviews/use-reviews-inbox';
import { formatScheduledPublishLabel } from '@/core/lib/format-scheduled-publish-label';

function DetectedChips({
  review,
  label,
}: {
  review: ReviewWithReplyDto;
  label: string;
}) {
  const chips = [
    ...new Set([...review.aspects.map((a) => a.label), ...review.topics]),
  ];
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground text-[11.5px]">{label}</span>
      {chips.map((chip) => (
        <Badge key={chip} variant="secondary" className="font-medium">
          {chip}
        </Badge>
      ))}
    </div>
  );
}

function PublishedReply({
  inbox,
  review,
  onEdit,
}: {
  inbox: ReviewsInbox;
  review: ReviewWithReplyDto;
  onEdit: () => void;
}) {
  const r = inbox.reviewsDict.reply;
  const reply = review.reply!;
  const draft = review.replyDraft;
  const status =
    reply.status === 'published'
      ? r.statusPublished
      : reply.status === 'pending'
        ? formatScheduledPublishLabel(
            typeof draft?.publishDueAt === 'string'
              ? draft.publishDueAt
              : undefined,
            inbox.locale,
            r,
          )
        : r.statusFailed;
  const variant =
    reply.status === 'failed'
      ? 'destructive'
      : reply.status === 'pending'
        ? 'warning'
        : 'success';
  const deleting = inbox.isDeleting(review.id);

  return (
    <Card className="flex flex-col gap-3 p-4 shadow-none sm:p-[18px]">
      <div className="flex flex-wrap items-center gap-2">
        <Reply className="text-muted-foreground size-4" aria-hidden />
        <h3 className="text-[13px] font-semibold">{r.yourReply}</h3>
        <Badge variant={variant}>{status}</Badge>
        {reply.createdByAI ? (
          <Badge variant="outline">{r.aiAuthored}</Badge>
        ) : null}
        {reply.autoPublished ? (
          <Badge variant="outline">{r.autoPublished}</Badge>
        ) : null}
      </div>
      <p className="text-sm leading-[1.65]">{reply.text}</p>
      {inbox.canReply ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1.5 size-3.5" aria-hidden />
            {reply.status === 'failed' ? r.retry : r.editReply}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive ml-auto"
            disabled={deleting}
            onClick={() => inbox.removeReply(review)}
          >
            {deleting ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="mr-1.5 size-3.5" aria-hidden />
            )}
            {r.deleteReply}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/** Full review + reply workspace for one review. Mount with `key={review.id}`. */
export function ReviewDetail({
  inbox,
  review,
  onPublished,
  className,
}: {
  inbox: ReviewsInbox;
  review: ReviewWithReplyDto;
  /** Fired after a reply was published (mobile sheet closes itself). */
  onPublished?: () => void;
  className?: string;
}) {
  const t = inbox.reviewsDict.detail;
  const [editing, setEditing] = useState(false);
  const urgent = isUrgentReview(review);
  const hasReply = Boolean(review.reply?.text);
  const locationName = inbox.locationNameById.get(review.locationId);

  const statusLabel = hasReply
    ? t.answered
    : urgent
      ? t.criticalUnanswered
      : t.unanswered;

  return (
    <div className={cn('flex min-w-0 flex-col gap-5', className)}>
      <div className="flex items-center gap-3.5">
        <ReviewAuthorAvatar
          name={review.authorName}
          className="size-11 text-sm"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-bold">
            {review.authorName}
          </div>
          <div className="text-muted-foreground mt-0.5 truncate text-[12.5px]">
            {[
              t.source,
              locationName,
              formatRelativeTime(review.reviewedAt, inbox.locale),
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <StarRating rating={review.rating} />
          <div
            className={cn(
              'mt-0.5 text-[11.5px] font-semibold',
              urgent && !hasReply
                ? 'text-destructive'
                : hasReply
                  ? 'text-[color:var(--ok-fg)]'
                  : 'text-muted-foreground',
            )}
          >
            {statusLabel}
          </div>
        </div>
      </div>

      {review.text ? (
        <p className="text-[17px] leading-[1.6] tracking-[-0.005em] text-pretty">
          {'„'}
          {review.text}
          {'“'}
        </p>
      ) : (
        <p className="text-muted-foreground/80 text-[15px] italic">
          {t.ratingOnly}
        </p>
      )}

      <DetectedChips review={review} label={t.detected} />

      {hasReply && !editing ? (
        <PublishedReply
          inbox={inbox}
          review={review}
          onEdit={() => setEditing(true)}
        />
      ) : !inbox.canReply ? null : (
        <>
          {review.replyDraft?.text && !hasReply ? (
            <p className="text-muted-foreground -mb-2 flex items-center gap-1.5 text-xs">
              <Sparkles
                className="size-3.5 text-[color:var(--ghost-accent-fg)]"
                aria-hidden
              />
              {inbox.reviewsDict.reply.draftReady}
            </p>
          ) : null}
          <ReviewReplyComposer
            inbox={inbox}
            review={review}
            initialText={review.replyDraft?.text ?? review.reply?.text ?? ''}
            onDone={() => {
              setEditing(false);
              onPublished?.();
            }}
          />
        </>
      )}
    </div>
  );
}
